import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  Paper,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { ReactFormBuilder } from 'react-form-builder2';
import 'react-form-builder2/dist/app.css';
import 'bootstrap/dist/css/bootstrap.min.css';
import './FormBuilder.css';

// Import form service functions (to be created)
import { getFormById, createForm, updateForm } from '../../services/formService';

const FormBuilder = () => {
  const navigate = useNavigate();
  const { formId } = useParams();
  const [formMeta, setFormMeta] = useState({ name: '', description: '' });
  const [builderData, setBuilderData] = useState([]);
  const [metaDialogOpen, setMetaDialogOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const loadForm = useCallback(async () => {
    try {
      setLoading(true);
      const formData = await getFormById(formId);
      if (formData) {
        setFormMeta({
          name: formData.name,
          description: formData.description || ''
        });
        const formDataArray = Array.isArray(formData.formData) ? formData.formData : [];
        setBuilderData(formDataArray);
      } else {
        setError('Form not found');
      }
    } catch (err) {
      console.error('Error loading form:', err);
      setError('Failed to load form');
    } finally {
      setLoading(false);
    }
  }, [formId]);

  useEffect(() => {
    if (formId) {
      loadForm();
    }
  }, [formId, loadForm]);

  const handleMetaChange = (e) => {
    const { name, value } = e.target;
    setFormMeta(prev => ({ ...prev, [name]: value }));
  };

  const handleBuilderChange = (data) => {
    const taskData = data?.task_data;
    const safeData = Array.isArray(taskData) ? taskData : [];
    setBuilderData(safeData);
  };

  const handleSave = () => {
    if (!formMeta.name) {
      setMetaDialogOpen(true);
    } else {
      saveForm();
    }
  };

  const saveForm = async () => {
    try {
      setLoading(true);
      const formData = {
        ...formMeta,
        formData: builderData,
        updatedAt: new Date()
      };

      if (formId) {
        await updateForm(formId, formData);
      } else {
        formData.createdAt = new Date();
        await createForm(formData);
      }

      navigate('/admin/forms');
    } catch (err) {
      console.error('Error saving form:', err);
      setError('Failed to save form');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return <Typography>Loading...</Typography>;
  }

  if (error) {
    return <Typography color="error">{error}</Typography>;
  }

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" component="h1">
          {formId ? 'Edit Form' : 'Create New Form'}
        </Typography>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button
            variant="outlined"
            onClick={() => navigate('/admin/forms')}
            startIcon={<ArrowBackIcon />}
          >
            Back to Forms List
          </Button>
          <Button
            variant="contained"
            color="primary"
            onClick={handleSave}
            disabled={loading}
          >
            {formId ? 'Update Form' : 'Save Form'}
          </Button>
        </Box>
      </Box>

      <Paper variant="outlined" sx={{ p: 2, mb: 3, width: '100%', minWidth: '1200px', maxWidth: 'none', boxSizing: 'border-box' }}>
        <ReactFormBuilder
          onPost={handleBuilderChange}
          data={Array.isArray(builderData) ? builderData : []}
        />
      </Paper>

      <Dialog open={metaDialogOpen} onClose={() => setMetaDialogOpen(false)}>
        <DialogTitle>Form Details</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Form Name"
            name="name"
            value={formMeta.name}
            onChange={handleMetaChange}
            fullWidth
            required
            error={!formMeta.name}
            helperText={!formMeta.name ? 'Form name is required' : ''}
          />
          <TextField
            margin="dense"
            label="Description"
            name="description"
            value={formMeta.description}
            onChange={handleMetaChange}
            fullWidth
            multiline
            rows={2}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setMetaDialogOpen(false)}>Cancel</Button>
          <Button 
            onClick={saveForm} 
            variant="contained"
            disabled={!formMeta.name}
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default FormBuilder; 